/*  ========================================================================
    # Sections - Leporello
    ========================================================================  */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import DatePicker, { registerLocale } from 'react-datepicker';
import de from 'date-fns/locale/de';
import en from 'date-fns/locale/en-GB';
import useLocalization from '../../hooks/useLocalization';
import LeporelloMonthsSlider from '../../components/Leporello/LeporelloMonthsSlider';
import useTranslation from '../../hooks/useTranslation';
import LeporelloResults from '../../components/Leporello/LeporelloResults';
import useGetParams from '../../hooks/useGetParams';
import useHistoryValues from '../../hooks/useHistoryValues';

registerLocale('de', de);
registerLocale('en', en);

const Leporello = ({
  title,
  customPaginationButton,
  showMonths,
  mode,
  type,
  numberOfInitialResults,
  hideInfoButtons,
  hideTicketButtons,
  searchQuery,
  filters,
  showGlobalPeriod,
  globalPeriod,
  includedProductions,
  singleEventIncludedSingleEvents,
  singleEventExcludedSingleEvents,
  customSelectedStartDate,
  customSelectedEndDate
}) => {
  const local = useLocalization();
  const locale = document.documentElement.lang;
  const _ = useTranslation();
  const getParams = useGetParams();
  const paramDate = _('param_leporello_date');
  const backendLocale = 'en-US';
  const today = new Date();

  // Define default date values depending on url params
  const defaultMonthKey =
    getParams[paramDate] && Object.keys(getParams).length > 0 && showMonths
      ? parseFloat(getParams[paramDate].split('-')[2])
      : 0;
  const defaultYear =
    getParams[paramDate] && Object.keys(getParams).length > 0 && showMonths
      ? getParams[paramDate].split('-')[1]
      : today.toLocaleDateString(backendLocale, { year: 'numeric' });
  const defaultMonth =
    getParams[paramDate] && Object.keys(getParams).length > 0 && showMonths
      ? getParams[paramDate].split('-')[0]
      : today.toLocaleDateString(backendLocale, { month: '2-digit' });

  const [showAllDirectly, setShowAllDirectly] = useState(!!defaultMonthKey);
  const [selectedKey, setSelectedKey] = useState(defaultMonthKey);
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [datePickerSelection, setDatePickerSelection] = useState(null);
  const [allHasLoaded, setAllHasLoaded] = useState(false);
  const [triggerEvent, setTriggerEvent] = useState(null);
  let selectedStartDate = new Date(selectedYear, selectedMonth - 1);
  let selectedEndDate = new Date(
    selectedStartDate.getFullYear(),
    selectedStartDate.getMonth() + 1,
    0
  );


  // Selected Date handling
  if (customSelectedStartDate && customSelectedEndDate) {
    selectedStartDate = new Date(customSelectedStartDate);
    selectedEndDate = new Date(customSelectedEndDate);
  } else {
    selectedStartDate = new Date(selectedYear, selectedMonth - 1);
    selectedEndDate = new Date(
      selectedStartDate.getFullYear(),
      selectedStartDate.getMonth() + 1,
      0
    );
  }

  const calendarIcon = `${local('svgSprite')}#calendar`;
  const arrowRightIcon = `${local('svgSprite')}#arrow-right`;
  const infoIcon = `${local('svgSprite')}#info`;
  const monthCount = 11;

  // Get all months from startDate with up to 12 months
  const getMonths = () => {
    const months = [];
    const date = new Date(`${today.getFullYear()}/${today.getMonth() + 1}/01`);

    months.push({
      month: date.toLocaleDateString(locale, { month: '2-digit' }),
      year: date.toLocaleDateString(locale, { year: 'numeric' }),
      monthName: date.toLocaleDateString(locale, { month: 'short' })
    });

    for (let index = 0; index < monthCount; index++) {
      date.setMonth(date.getMonth() + 1);
      months.push({
        month: date.toLocaleDateString(locale, { month: '2-digit' }),
        year: date.toLocaleDateString(locale, { year: 'numeric' }),
        monthName: date.toLocaleDateString(locale, { month: 'short' })
      });
    }

    return months;
  };

  const sliderMonths = getMonths();

  const handleHistoryPop = values => {
    if (values) {
      const params = values[paramDate].split('-');
      setSelectedMonth(params[0]); // month
      setSelectedYear(params[1]); // year
      setSelectedKey(parseFloat(params[2])); // month key for slider
    }
  };

  // Set default history params
  const pushNewParamsToUrl = useHistoryValues(
    {
      [paramDate]: defaultMonth + '-' + defaultYear + '-' + defaultMonthKey
    },
    handleHistoryPop
  );

  // Handle Datepicker day click
  const handleDatePickerOnChange = date => {
    if (date.getMonth() !== selectedStartDate.getMonth()) {
      const newMonth = date.toLocaleDateString('en-US', { month: '2-digit' });
      const newYear = date.toLocaleDateString('en-US', { year: 'numeric' });

      // Set new start date states
      setSelectedMonth(newMonth);
      setSelectedYear(newYear);
      const newKey = sliderMonths.findIndex(item => {
        return item.month === newMonth && item.year === newYear;
      });
      setSelectedKey(newKey);
      pushNewParamsToUrl({
        [paramDate]: newMonth + '-' + newYear + '-' + newKey
      });
    }
    setShowAllDirectly(true);
    setDatePickerSelection(date);
  };

  // Handle Month click
  const handleMonthChange = (date, event) => {
    // Set new start date states and clear datepicker selection
    setDatePickerSelection(null);
    setShowAllDirectly(true);
    setSelectedMonth(date.month);
    setSelectedYear(date.year);
    setSelectedKey(date.monthKey);
    setTriggerEvent(event ? event : null);
    pushNewParamsToUrl({
      [paramDate]: date.month + '-' + date.year + '-' + date.monthKey
    });
  };

  // Check if period should display
  const showPeriodInfo = () => {
    if (showMonths && showGlobalPeriod && globalPeriod['show_period_info']) {

      // check if displayed month is in period
      const startDateTimestamp = Date.parse(globalPeriod['start_date'].slice(0, -3));
      const endDateTimestamp = Date.parse(globalPeriod['end_date'].slice(0, -3));
      const selectedDateTimestamp = Date.parse(`${selectedYear}-${selectedMonth}`);

      if (selectedDateTimestamp >= startDateTimestamp && selectedDateTimestamp <= endDateTimestamp) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  // Handle after all results have loaded
  const handleAllResultsLoaded = () => {
    // Scroll to selected date if applicable
    if (!allHasLoaded) {
      setAllHasLoaded(true);
    }
  };

  return (
    <div className='s-leporello__wrapper'>
      <div className='container'>
        <div className='s-leporello__header'>
          <h2 className='s-leporello__title'>{title}</h2>
          {showMonths && (
            <>
              <div className='s-leporello__secondary-action'>
                <DatePicker
                  selected={today > selectedStartDate ? today : selectedStartDate}
                  minDate={new Date()}
                  maxDate={new Date(new Date().setMonth(new Date().getMonth() + 11))}
                  showDisabledMonthNavigation
                  onChange={handleDatePickerOnChange}
                  locale={locale}
                  customInput={
                    <button
                      type='button'
                      className='c-button c-button--link c-button--icon c-button--icon-black s-leporello__select-date'
                    >
                      {_('leporello_select_date')}
                      <svg viewBox='0 0 200 200'>
                        <use href={calendarIcon}></use>
                      </svg>
                    </button>
                  }
                />
              </div>
              <LeporelloMonthsSlider
                months={sliderMonths}
                selectedKey={selectedKey}
                onMonthChange={handleMonthChange}
                selectedMonth={selectedMonth}
                selectedYear={selectedYear}
              />
            </>
          )}
          {!showMonths && customPaginationButton && (
            <div className='s-leporello__secondary-action'>
              <a
                href={customPaginationButton.url}
                target={customPaginationButton.target}
                className='c-button c-button--link c-button--icon c-button--icon-black'
                aria-label={customPaginationButton.title}
              >
                {customPaginationButton.title}
                <svg viewBox='0 0 200 200'>
                  <use href={arrowRightIcon}></use>
                </svg>
              </a>
            </div>
          )}
        </div>
        {showPeriodInfo() ?
          <div className='s-leporello__period-info'>
            <div className='s-leporello__period-info-icon'>
              <svg viewBox='0 0 200 200'>
                <use href={infoIcon} />
              </svg>
            </div>
            <div className='s-leporello__period-info-content' dangerouslySetInnerHTML={{ __html: globalPeriod['period_info'] }} />
          </div>
          : null}
        <LeporelloResults
          onAllHasLoaded={handleAllResultsLoaded}
          startDate={today > selectedStartDate ? today : selectedStartDate}
          endDate={showMonths || (customSelectedEndDate && selectedEndDate) ? selectedEndDate : null}
          numberOfInitialResults={showAllDirectly && mode === 'DAYS' ? -1 : numberOfInitialResults}
          scrollToDate={datePickerSelection}
          triggerEvent={triggerEvent}
          {...{
            customPaginationButton,
            hideInfoButtons,
            hideTicketButtons,
            mode,
            type,
            searchQuery,
            includedProductions,
            singleEventIncludedSingleEvents,
            singleEventExcludedSingleEvents
          }}
          {...filters}
          periodStartDate={showGlobalPeriod ? globalPeriod['start_date'] : false}
          periodEndDate={showGlobalPeriod ? globalPeriod['end_date'] : false}
          periodReport={showGlobalPeriod ? globalPeriod['report'] : false}
        />
        {showMonths && (allHasLoaded || showAllDirectly) ? (
          <div className="s-leporello__footer">
            <LeporelloMonthsSlider
              months={sliderMonths}
              selectedKey={selectedKey}
              onMonthChange={handleMonthChange}
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

Leporello.propTypes = {
  title: PropTypes.string,
  customPaginationButton: PropTypes.object,
  showMonths: PropTypes.bool,
  hideInfoButtons: PropTypes.bool,
  hideTicketButtons: PropTypes.bool,
  mode: PropTypes.oneOf(['DAYS', 'ITEMS']).isRequired,
  type: PropTypes.string,
  numberOfInitialResults: PropTypes.number,
  searchQuery: PropTypes.string,
  filters: PropTypes.shape({
    singleEventIncludedSingleEvents: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
    singleEventExcludedSingleEvents: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
    singleEventIncludeTags: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
    singleEventExcludeTags: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
    performanceIncludeProductions: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
    performanceExcludeProductions: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
    includeLocations: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
    excludeLocations: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
    performancesInclude: PropTypes.oneOfType([PropTypes.array, PropTypes.bool])
  }),
  showGlobalPeriod: PropTypes.bool,
  globalPeriod: PropTypes.object,
  includedProductions: PropTypes.array,
  selectedStartDate: PropTypes.string,
  selectedEndDate: PropTypes.string
};

export default Leporello;
